import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Camp de text que es desa sol.
 *
 * Va néixer per al backcasting i des de l'11/08/2026 també l'usa la passa 1 de
 * la construcció d'escenaris; per això viu a `components/` i no dins de cap
 * carpeta de funcionalitat.
 *
 * Tres coses que resol, i totes tres surten de problemes que ja hem tingut:
 *
 * 1. **El valor viu a l'estat local mentre s'escriu.** Si el component es
 *    tornés a pintar amb el que hi ha al servidor a cada tecla —que és el que
 *    passava a la passa 4 del constructor d'escenaris— el cursor salta i les
 *    lletres se substitueixen entre elles. Aquí el que ve de fora només es
 *    torna a agafar si el camp **no** té el focus.
 *
 * 2. **Es desa en sortir del camp**, no a cada tecla i no amb un botó. Un botó
 *    de desar per camp en una pantalla de vint camps no el prem ningú.
 *
 * 3. **No hi ha cap toast quan va bé.** El «desat» és una paraula grisa que
 *    s'esvaeix; els avisos flotants només queden per als errors, que és
 *    l'única cosa que demana atenció.
 */
const CampText = ({
  label, value, onSave, placeholder, hint,
  multiline = false, rows = 3, className = '', inputClassName = '', disabled = false
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value ?? '');
  const [desant, setDesant] = useState(false);
  const [desat, setDesat] = useState(false);
  const teFocus = useRef(false);
  const temporitzador = useRef(null);

  useEffect(() => {
    if (!teFocus.current) setText(value ?? '');
  }, [value]);

  useEffect(() => () => clearTimeout(temporitzador.current), []);

  const desa = async () => {
    teFocus.current = false;
    const net = text ?? '';
    if (net === (value ?? '')) return;
    setDesant(true);
    try {
      await onSave(net);
      setDesat(true);
      clearTimeout(temporitzador.current);
      temporitzador.current = setTimeout(() => setDesat(false), 2000);
    } finally {
      setDesant(false);
    }
  };

  const comu = {
    value: text,
    disabled,
    placeholder,
    onFocus: () => { teFocus.current = true; },
    onChange: (e) => setText(e.target.value),
    onBlur: desa,
    className:
      'w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 ' +
      'placeholder-neutral-400 focus:border-accent-500 focus:outline-none focus:ring-2 ' +
      'focus:ring-accent-500/20 disabled:bg-neutral-50 disabled:text-neutral-500 ' + inputClassName
  };

  return (
    <div className={className}>
      {label && (
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <label className="text-xs font-semibold text-neutral-600">{label}</label>
          <span className="text-xs text-neutral-400" aria-live="polite">
            {desant ? t('backcasting.saving') : desat ? t('backcasting.saved') : ''}
          </span>
        </div>
      )}
      {multiline
        ? <textarea rows={rows} {...comu} />
        : <input type="text" {...comu} />}
      {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
};

export default CampText;
